import Image from 'next/image';
import { useState } from 'react';
import { ModalStyle } from '../../styles/components/common/ui';
import { OrderProcessGetReviewStyle } from '../../styles/components/order-process';
import {
  Button,
  Modal,
  ModalContent,
  ModalFooter,
  ModalHeader,
} from '../common/ui';

export interface IOrderProcessReviewLinkButton {
  label: string;
  link: string;
  iconUrl: string;
}
export interface IOrderProcessGetReviewProps {
  iconUrl?: string;
  title: string;
  message: string;
  ratingTitle: string;
  ratingMessage: string;
  ratingRequiredMessage: string;
  accountButtonLabel: string;
  nextButtonLabel: string;
  reviewModalTitle: string;
  reviewModalMessage: string;
  reviewModalContentPlacheholder: string;
  reviewModalChangeRatingButtonLabel: string;
  reviewModalOmitButtonLabel: string;
  reviewModalSendButtonLabel: string;
  reviewModalSentSuccessfullyLabel: string;
  shareModalTitle: string;
  shareModalMessage: string;
  shareModalExternalLinkButtons: IOrderProcessReviewLinkButton[];
  shareModalTip: string;
  shareModalCloseButtonLabel: string;
  score?: number;
  showRatingRequiredMessage?: boolean;
  showModal?: boolean;
  sentSuccessfully?: boolean;
  showControls?: boolean;
  showNextButton?: boolean;
  onGoToAccountClick: () => void;
  onNextClick: () => void;
  onRatingChange: (score: number) => void;
  onCloseModal: () => void;
  onSend: (reviewText: string) => void;
}

const OrderProcessGetReview = (props: IOrderProcessGetReviewProps) => {
  const [step, setStep] = useState<'review' | 'share' | 'finish'>('review');

  const handleStarClick = (star: number) => {
    if (step !== 'finish') {
      props.onRatingChange(star);
    }
  };

  const handleDismissModal = () => {
    props.onCloseModal();
  };

  const handleOmitReview = () => {
    setStep('share');
  };

  const handleShareClose = () => {
    setStep('finish');
    props.onCloseModal();
  };

  const handleSendReview = (reviewText: string) => {
    props.onSend(reviewText);
    setStep('share');
  };

  const currentScore = props.score || 0;

  return (
    <div className={OrderProcessGetReviewStyle['order-process-get-review']}>
      <Image
        quality={90}
        src={props.iconUrl || '/assets/igerent-logo-sq.svg'}
        alt="iGerent logo"
        priority={false}
        width={80}
        height={80}
      />
      <h1>{props.title}</h1>
      <p>{props.message}</p>

      {props.showControls && (
        <>
          <div
            className={
              OrderProcessGetReviewStyle['order-process-get-review-divider']
            }
          />
          <h3>{props.ratingTitle}</h3>
          <p>{props.ratingMessage}</p>
          <div
            className={
              OrderProcessGetReviewStyle['order-process-get-review-stars']
            }>
            {[1, 2, 3, 4, 5].map((star) => {
              const isFilled = star <= currentScore;
              return (
                <button
                  key={star}
                  type="button"
                  className={`${
                    OrderProcessGetReviewStyle['order-process-get-review-star']
                  } ${
                    step !== 'finish'
                      ? OrderProcessGetReviewStyle[
                          'order-process-get-review-star-selectable'
                        ]
                      : ''
                  }`}
                  onClick={() => handleStarClick(star)}
                  aria-label={`Rate ${star} stars`}>
                  <Image
                    key={`star-${star}-${isFilled}`}
                    src={
                      isFilled
                        ? '/assets/images/icons/StarSolid.svg'
                        : '/assets/images/icons/StarEmpty.svg'
                    }
                    alt=""
                    width={48}
                    height={48}
                    className="colorize-primary"
                  />
                </button>
              );
            })}
          </div>
          {props.showRatingRequiredMessage && (
            <div
              className={
                OrderProcessGetReviewStyle[
                  'order-process-get-review-rating-required'
                ]
              }>
              <p>{props.ratingRequiredMessage}</p>
            </div>
          )}
          <div
            className={
              OrderProcessGetReviewStyle['order-process-get-review-buttons']
            }>
            <Button color="cta-gray" onClick={props.onGoToAccountClick}>
              {props.accountButtonLabel}
            </Button>
            {step !== 'finish' && props.showNextButton && (
              <Button color="cta-primary-inverted" onClick={props.onNextClick}>
                {props.nextButtonLabel}
              </Button>
            )}
          </div>
        </>
      )}

      {/* Modal */}
      <Modal show={props.showModal}>
        <ModalHeader>
          {step === 'share' && props.sentSuccessfully ? (
            <div
              className={
                OrderProcessGetReviewStyle['order-process-get-review-success']
              }>
              <Image
                quality={90}
                src={'/assets/images/icons/CheckmarkIconGreen.svg'}
                alt="Success"
                width={32}
                height={32}
              />
              <span>{props.reviewModalSentSuccessfullyLabel}</span>
            </div>
          ) : (
            <span></span>
          )}
          <Image
            quality={90}
            src={'/assets/images/icons/CloseIcon.svg'}
            className={ModalStyle['close-icon']}
            alt="Close"
            width={32}
            height={32}
            onClick={handleDismissModal}
          />
        </ModalHeader>
        <ModalContent>
          {step === 'review' && (
            <div
              className={`${OrderProcessGetReviewStyle['order-process-get-review']} ${OrderProcessGetReviewStyle['order-process-get-review-inline']}`}>
              <Image
                quality={90}
                src={'/assets/igerent-logo-sq.svg'}
                alt="iGerent logo"
                priority={false}
                width={80}
                height={80}
              />
              <h1>{props.reviewModalTitle}</h1>
              <p>{props.reviewModalMessage}</p>
              <div
                className={
                  OrderProcessGetReviewStyle['order-process-get-review-stars']
                }>
                {[1, 2, 3, 4, 5].map((star) => {
                  const isFilled = star <= currentScore;
                  return (
                    <button
                      key={star}
                      type="button"
                      className={
                        OrderProcessGetReviewStyle[
                          'order-process-get-review-star-small'
                        ]
                      }
                      aria-label={`Rate ${star} stars`}>
                      <Image
                        key={`star-${star}-${isFilled}`}
                        src={
                          isFilled
                            ? '/assets/images/icons/StarSolid.svg'
                            : '/assets/images/icons/StarEmpty.svg'
                        }
                        alt=""
                        width={48}
                        height={48}
                        className="colorize-primary"
                      />
                    </button>
                  );
                })}
              </div>
              <textarea
                placeholder={props.reviewModalContentPlacheholder}
                name="review-comment"
                id="review-comment"
              />
            </div>
          )}

          {step === 'share' && (
            <div
              className={`${OrderProcessGetReviewStyle['order-process-get-review']} ${OrderProcessGetReviewStyle['order-process-get-review-inline']}`}>
              <Image
                quality={90}
                src={'/assets/igerent-logo-sq.svg'}
                alt="iGerent logo"
                priority={false}
                width={80}
                height={80}
              />
              <h1>{props.shareModalTitle}</h1>
              <p>{props.shareModalMessage}</p>
              <div
                className={
                  OrderProcessGetReviewStyle[
                    'order-process-get-review-share-options'
                  ]
                }>
                {props.shareModalExternalLinkButtons.map((button, index) => (
                  <a
                    key={index}
                    href={button.link}
                    target="_blank"
                    rel="noopener noreferrer"
                    className={
                      OrderProcessGetReviewStyle[
                        'order-process-get-review-share-button'
                      ]
                    }>
                    <Image
                      src={button.iconUrl}
                      alt={button.label}
                      width={24}
                      height={24}
                    />
                    <span>{button.label}</span>
                  </a>
                ))}
              </div>
              <p
                className={
                  OrderProcessGetReviewStyle[
                    'order-process-get-review-share-tip'
                  ]
                }>
                {props.shareModalTip}
              </p>
            </div>
          )}
        </ModalContent>
        <ModalFooter>
          {step === 'review' && (
            <div
              className={
                OrderProcessGetReviewStyle['order-process-get-review-buttons']
              }>
              <Button color="cta-subtle" onClick={handleDismissModal}>
                {props.reviewModalChangeRatingButtonLabel}
              </Button>
              <Button color="cta-gray" onClick={handleOmitReview}>
                {props.reviewModalOmitButtonLabel}
              </Button>
              <Button
                color="cta-primary-inverted"
                onClick={() =>
                  handleSendReview(
                    (
                      document.getElementById(
                        'review-comment'
                      ) as HTMLTextAreaElement
                    )?.value || ''
                  )
                }>
                {props.reviewModalSendButtonLabel}
              </Button>
            </div>
          )}

          {step === 'share' && (
            <div
              className={
                OrderProcessGetReviewStyle['order-process-get-review-buttons']
              }>
              <Button color="cta-subtle" onClick={handleShareClose}>
                {props.shareModalCloseButtonLabel}
              </Button>
              <Button color="cta-gray" onClick={props.onGoToAccountClick}>
                {props.accountButtonLabel}
              </Button>
            </div>
          )}
        </ModalFooter>
      </Modal>
    </div>
  );
};

export default OrderProcessGetReview;
